/* FernFawn Native v2. Hyper integration. */
(() => {
  'use strict';

  const $ = (root, selector) => root?.querySelector(selector);

  const $$ = (root, selector) => [
    ...(root?.querySelectorAll(selector) || [])
  ];

  const sizeKey = value =>
    (String(value || '').match(/\d+(?:\.\d+)?/g) || [])
      .slice(0, 2)
      .map(Number)
      .sort((a, b) => a - b)
      .join('x');

  const el = (tag, value, cls = '') =>
    Object.assign(document.createElement(tag), {
      textContent: value,
      className: cls
    });

  const setText = (node, value) => {
    if (node && node.textContent !== value) {
      node.textContent = value;
    }
  };

  class BlanketNative extends HTMLElement {
    connectedCallback() {
      this.start = () => this.mount();

      if (document.readyState === 'loading') {
        document.addEventListener(
          'DOMContentLoaded',
          this.start,
          { once: true }
        );
      } else {
        this.mount();
      }
    }

    disconnectedCallback() {
      document.removeEventListener(
        'DOMContentLoaded',
        this.start
      );

      this.events?.abort();
      this.observer?.disconnect();
      this.unsubscribe?.();

      cancelAnimationFrame(this.frame);
      this.notice?.remove();
      this.error?.remove();

      this.wallets?.forEach(node => {
        node.inert = false;
      });

      this.mounted = false;
    }

    on(node, event, callback, capture = false) {
      node?.addEventListener(event, callback, {
        capture,
        signal: this.events.signal
      });
    }

    mount() {
      if (this.mounted || !this.isConnected) return;

      this.main = document.getElementById(
        `MainProduct-${this.dataset.section}`
      );

      this.form = $(this.main, 'form.main-product-form');

      if (!this.form) return;

      if (
        $$(this.main, 'ff-blanket-native').some(
          node => node !== this && node.mounted
        )
      ) {
        this.closest('.product__block')
          ?.classList.add('ffn-setup-block');

        return;
      }

      this.mounted = true;
      this.events = new AbortController();
      this.wallets = new Set();

      this.variants = JSON.parse(
        $(this, '[data-ffn-variants]').textContent
      );

      this.options = JSON.parse(
        $(this, '[data-ffn-options]').textContent
      );

      this.sizeIndex = this.options.findIndex(
        name =>
          name.toLowerCase() ===
          (this.dataset.sizeName || 'Sizes').toLowerCase()
      );

      if (this.sizeIndex < 0) {
        this.sizeIndex = this.options.findIndex(
          (_, i) =>
            this.variants.some(
              variant => sizeKey(variant.options[i]).includes('x')
            )
        );
      }

      this.money = cents =>
        new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: this.dataset.currency || 'USD',
          minimumFractionDigits: Number(cents) % 100 ? 2 : 0,
          maximumFractionDigits: 2
        }).format(Number(cents) / 100);

      this.hideSettingsBlocks();

      $$(this.main, '.ffn-gate-message').forEach(
        node => node.remove()
      );

      this.error = el('p', '', 'ffn-error');
      this.error.setAttribute('role', 'alert');
      this.error.hidden = true;

      const buttons =
        $(this.form, '.product-form__buttons') || this.form;

      buttons.after(this.error);

      this.on(this.main, 'change', event => {
        if (event.target.matches('[data-ffn-color]')) {
          this.syncGallery();
        }

        this.schedule();
      });

      this.on(this.main, 'input', () => this.schedule());

      this.on(
        this.form,
        'submit',
        event => {
          if (!this.guard()) {
            event.preventDefault();
            event.stopImmediatePropagation();
            this.showError();
          }
        },
        true
      );

      this.on(
        this.main,
        'click',
        event => {
          const button = event.target.closest(
            'button[name="add"]'
          );

          if (
            button?.form === this.form &&
            !this.guard()
          ) {
            event.preventDefault();
            event.stopImmediatePropagation();
            this.showError();
          }
        },
        true
      );

      const pubsub = window.FoxTheme?.pubsub;

      if (pubsub) {
        this.unsubscribe = pubsub.subscribe(
          pubsub.PUB_SUB_EVENTS.variantChange,
          ({ data }) => {
            if (
              String(data.sectionId) !==
              this.dataset.section
            ) {
              return;
            }

            if (data.variant) {
              const index = this.variants.findIndex(
                variant =>
                  String(variant.id) ===
                  String(data.variant.id)
              );

              if (index >= 0) {
                this.variants[index] = data.variant;
              }
            }

            this.sync();
          }
        );
      }

      this.observer = new MutationObserver(records => {
        const relevant = records.some(record => {
          const target =
            record.target.nodeType === 1
              ? record.target
              : record.target.parentElement;

          if (target?.closest('.uploadkit-upload-field')) {
            return true;
          }

          if (target?.matches('input[name="id"]')) {
            return true;
          }

          return [...record.addedNodes].some(
            node =>
              node.nodeType === 1 &&
              (
                node.matches(
                  '.uploadkit-upload-field,' +
                  '.shopify-payment-button,' +
                  'variant-selects,' +
                  '[data-ffn-size-notes]'
                ) ||
                node.querySelector(
                  '.uploadkit-upload-field,' +
                  '.shopify-payment-button,' +
                  'variant-selects,' +
                  '[data-ffn-size-notes]'
                )
              )
          );
        });

        if (relevant) this.schedule();
      });

      this.observer.observe(this.main, {
        subtree: true,
        childList: true,
        attributes: true,
        attributeFilter: [
          'value',
          'invalid',
          'aria-busy',
          'class'
        ]
      });

      this.sync();

      this.frame = requestAnimationFrame(
        () => this.sync()
      );
    }

    schedule() {
      if (this.queued) return;

      this.queued = true;

      queueMicrotask(() => {
        this.queued = false;

        if (this.isConnected) this.sync();
      });
    }

    hideSettingsBlocks() {
      this.closest('.product__block')
        ?.classList.add('ffn-setup-block');

      $$(this.main, '[data-ffn-size-notes]').forEach(
        node =>
          node.closest('.product__block')
            ?.classList.add('ffn-setup-block')
      );
    }

    current() {
      const id = $(this.form, '[name="id"]')?.value;

      return this.variants.find(
        variant => String(variant.id) === id
      );
    }

    sync() {
      this.hideSettingsBlocks();
      this.paintSizes();
      this.paintPrice();
      this.paintButton();
      this.syncGallery();
      this.guard();
    }

    paintPrice() {
      const variant = this.current();

      if (!variant) return;

      const row = $(this.main, '.ffn-price-row');

      if (!row) return;

      setText(
        $(row, '[data-ffn-price]'),
        this.money(variant.price)
      );

      const saving = Math.max(
        0,
        (variant.compare_at_price || 0) - variant.price
      );

      const compare = $(row, '[data-ffn-compare]');

      if (compare) {
        compare.hidden = !saving;

        setText(
          compare,
          this.money(variant.compare_at_price || 0)
        );
      }

      const offer = $(row, '[data-ffn-offer]');

      if (offer) {
        const copy =
          offer.dataset.manual ||
          (
            saving
              ? `${offer.dataset.saveLabel} ${this.money(saving)}`
              : ''
          );

        setText(offer, copy);
        offer.hidden = !copy;
      }
    }

    paintButton() {
      const variant = this.current();

      const button = $(
        this.form,
        'button[name="add"]'
      );

      if (
        !variant?.available ||
        !button ||
        button.disabled
      ) {
        return;
      }

      const quantity =
        Number(new FormData(this.form).get('quantity')) || 1;

      setText(
        $(button, '.btn__text'),
        `${this.dataset.button || 'Add to cart'} · ` +
        this.money(variant.price * quantity)
      );
    }

    paintSizes() {
      const current = this.current();

      if (!current || this.sizeIndex < 0) return;

      const fields = $$(
        this.main,
        'variant-selects fieldset'
      );

      const field =
        fields.find(
          node => node.hasAttribute('data-ff-size-option')
        ) ||
        fields[this.sizeIndex];

      if (!field) return;

      field.classList.add('ffn-sizes');

      const notes = $$(
        this.main,
        '[data-ffn-size-notes] span'
      ).map(node => node.dataset);

      $$(field, 'input[type="radio"]').forEach(input => {
        const label = input.nextElementSibling;

        if (!label?.matches('label')) return;

        const match = this.variants.find(
          variant =>
            variant.options[this.sizeIndex] === input.value &&
            variant.options.every(
              (value, index) =>
                index === this.sizeIndex ||
                value === current.options[index]
            )
        );

        const key = sizeKey(input.value);

        const note = notes.find(
          item => sizeKey(item.key) === key
        );

        const price = match
          ? this.money(match.price)
          : 'Unavailable';

        const badge = key === '50x60'
          ? (this.dataset.featured || '')
          : '';

        const signature = [
          input.value,
          note?.label,
          note?.use,
          price,
          badge
        ].join('|');

        if (
          label.dataset.ffnSignature === signature
        ) {
          return;
        }

        label.dataset.ffnSignature = signature;

        const row = el('span', '', 'ffn-size-row');
        const copy = el('span', '', 'ffn-size-copy');
        const top = el('span', '', 'ffn-size-top');

        top.append(
          el('strong', note?.label || input.value)
        );

        if (note?.label) {
          top.append(
            el(
              'span',
              input.value.replace(/\s*x\s*/i, ' × '),
              'ffn-size-dimension'
            )
          );
        }

        if (badge) {
          top.append(
            el('em', badge, 'ffn-size-badge')
          );
        }

        copy.append(top);

        if (note?.use) {
          copy.append(
            el('small', note.use, 'ffn-size-use')
          );
        }

        row.append(
          copy,
          el('strong', price, 'ffn-size-price')
        );

        label.replaceChildren(row);
      });
    }

    syncGallery() {
      const chosen = $(
        this.main,
        '[data-ffn-color]:checked'
      );

      setText(
        $(this.main, '[data-ffn-color-name]'),
        chosen?.value || ''
      );

      const detail = {
        color: chosen?.dataset.key || '',
        size: sizeKey(
          this.current()?.options[this.sizeIndex]
        )
      };

      const signature =
        `${detail.color}|${detail.size}`;

      if (
        signature === this.selectionSignature
      ) {
        return;
      }

      this.selectionSignature = signature;

      this.main.dispatchEvent(
        new CustomEvent('ffn:selection', { detail })
      );
    }

    photoReady() {
      if (
        this.dataset.requirePhoto === 'false'
      ) {
        return true;
      }

      const fields = $$(
        this.main,
        '.uploadkit-upload-field'
      );

      const field = fields.find(node => {
        const name =
          $(node, '[data-uploader-name]')
            ?.dataset.uploaderName || '';

        return this.dataset.uploadName
          ? name === this.dataset.uploadName
          : !/^Blanket [234] photo$/.test(name);
      });

      if (
        !field ||
        field.hidden ||
        field.classList.contains('uploadkit-invalid') ||
        field.getAttribute('invalid') === 'true' ||
        field.getAttribute('aria-busy') === 'true' ||
        $(field, '[aria-busy="true"],[invalid="true"]')
      ) {
        return false;
      }

      const inputs = $$(
        field,
        'input[name^="properties["]'
      ).filter(
        input =>
          !input.disabled &&
          /^https?:\/\//i.test(input.value.trim())
      );

      if (!inputs.length) return false;

      inputs.forEach(input => {
        if (input.form !== this.form) {
          input.setAttribute('form', this.form.id);
        }
      });

      const data = new FormData(this.form);

      return inputs.every(
        input =>
          data.getAll(input.name).includes(input.value)
      );
    }

    selectionPending() {
      const current = this.current();

      if (!current) return true;

      const selected = $$(
        this.main,
        'variant-selects fieldset input[type="radio"]:checked'
      ).map(input => input.value);

      return (
        selected.length === this.options.length &&
        !selected.every(
          (value, i) => value === current.options[i]
        )
      );
    }

    guard() {
      const pending = this.selectionPending();
      const variant = this.current();

      this.ready = Boolean(
        variant?.available &&
        !pending &&
        $(this.main, '[data-ffn-color]:checked') &&
        this.photoReady()
      );

      // UploadKit handles required-photo validation
      // for accelerated checkout.
      // This guard validates the native add-to-cart flow.
      if (this.ready) this.error.hidden = true;

      return this.ready;
    }

    showError() {
      let message = 'Please upload your pet’s photo.';
      let target = $(this.main, '.uploadkit-button');

      if (this.selectionPending()) {
        message = 'Please wait while your size updates.';

        target = $(
          this.main,
          'variant-selects input:checked'
        );
      } else if (!this.current()?.available) {
        message = 'Please choose an available size.';

        target = $(
          this.main,
          'variant-selects input:checked'
        );
      } else if (
        !$(this.main, '[data-ffn-color]:checked')
      ) {
        message = 'Please choose a blanket design.';
        target = $(this.main, '[data-ffn-color]');
      }

      setText(this.error, message);
      this.error.hidden = false;
      target?.focus();
    }
  }

  class BlanketGallery extends HTMLElement {
    connectedCallback() {
      this.start = () => this.mount();

      if (document.readyState === 'loading') {
        document.addEventListener(
          'DOMContentLoaded',
          this.start,
          { once: true }
        );
      } else {
        this.mount();
      }
    }

    disconnectedCallback() {
      document.removeEventListener(
        'DOMContentLoaded',
        this.start
      );

      this.events?.abort();
      this.mounted = false;
    }

    on(node, type, fn) {
      node?.addEventListener(type, fn, {
        signal: this.events.signal
      });
    }

    mount() {
      if (this.mounted || !this.isConnected) return;

      this.mounted = true;
      this.events = new AbortController();

      this.main = document.getElementById(
        `MainProduct-${this.dataset.section}`
      );

      this.images = $$(
        this,
        '[data-ffn-images] span'
      ).map(node => {
        const [description, tags] =
          (node.dataset.alt || '').split('|ffb|');

        const pairs = Object.fromEntries(
          (tags || '')
            .split(';')
            .filter(value => value.includes('='))
            .map(value =>
              value.trim().split('=').map(
                item => item.trim()
              )
            )
        );

        return {
          ...node.dataset,
          alt: description.trim(),
          color: pairs.color?.toLowerCase() || '',
          size: sizeKey(pairs.size)
        };
      });

      this.on(
        this.main,
        'ffn:selection',
        event => this.filter(event.detail)
      );

      this.on(
        $(this, '[data-ffn-prev]'),
        'click',
        () => this.show(this.index - 1)
      );

      this.on(
        $(this, '[data-ffn-next]'),
        'click',
        () => this.show(this.index + 1)
      );

      this.on(
        $(this, '.ffn-thumbs'),
        'click',
        event => {
          const button = event.target.closest('[data-index]');

          if (button) {
            this.show(Number(button.dataset.index));
          }
        }
      );

      const stage = $(this, '.ffn-stage');
      const dialog = $(this, 'dialog');

      this.on(stage, 'keydown', event => {
        if (
          event.key === 'ArrowRight' ||
          event.key === 'ArrowLeft'
        ) {
          event.preventDefault();

          this.show(
            this.index +
            (event.key === 'ArrowRight' ? 1 : -1)
          );
        }
      });

      this.on(stage, 'pointerdown', event => {
        this.pointer = {
          x: event.clientX,
          y: event.clientY
        };

        this.swiped = false;
      });

      this.on(stage, 'pointerup', event => {
        if (!this.pointer) return;

        const dx = event.clientX - this.pointer.x;
        const dy = event.clientY - this.pointer.y;

        this.pointer = null;

        if (
          Math.abs(dx) > 45 &&
          Math.abs(dx) > Math.abs(dy)
        ) {
          this.swiped = true;

          this.show(
            this.index + (dx < 0 ? 1 : -1)
          );
        }
      });

      this.on(stage, 'pointercancel', () => {
        this.pointer = null;
      });

      this.on(
        $(this, '[data-ffn-zoom]'),
        'click',
        () => {
          if (this.swiped) {
            this.swiped = false;
            return;
          }

          const picture = this.visible[this.index];

          if (!picture) return;

          Object.assign($(dialog, 'img'), {
            src: picture.large,
            alt: picture.alt
          });

          dialog.showModal();
        }
      );

      this.on(
        $(this, '[data-ffn-close]'),
        'click',
        () => dialog.close()
      );

      this.on(
        dialog,
        'close',
        () => $(this, '[data-ffn-zoom]')?.focus()
      );

      const chosen = $(
        this.main,
        '[data-ffn-color]:checked'
      );

      const sizeInput = $(
        this.main,
        '[data-ff-size-option] input:checked'
      );

      this.filter({
        color:
          chosen?.dataset.key ||
          this.dataset.defaultColor,

        size: sizeKey(sizeInput?.value)
      });
    }

    filter({ color, size }) {
      const tagged = this.images.some(
        picture => picture.color
      );

      this.visible = tagged
        ? this.images.filter(
            picture =>
              (
                picture.color === color ||
                picture.color === 'all'
              ) &&
              (
                !picture.size ||
                picture.size === size
              )
          ).sort(
            (a, b) =>
              Number(a.color === 'all') -
              Number(b.color === 'all')
          )
        : [...this.images];

      const signature = this.visible.map(
        picture => picture.id
      ).join(',');

      if (signature === this.signature) return;

      this.signature = signature;

      const thumbs = $(this, '.ffn-thumbs');
      thumbs.replaceChildren();

      this.visible.forEach((picture, index) => {
        const button = el('button', '');

        button.type = 'button';
        button.dataset.index = index;

        button.setAttribute(
          'aria-label',
          `Photo ${index + 1}: ` +
          (picture.alt || 'Blanket detail')
        );

        const thumb = new Image(64, 64);

        Object.assign(thumb, {
          src: picture.thumb,
          alt: '',
          loading: 'lazy',
          decoding: 'async'
        });

        button.append(thumb);
        thumbs.append(button);
      });

      this.show(0);
    }

    show(index) {
      const length = this.visible.length;

      this.index = length
        ? (index + length) % length
        : 0;

      $(this, '.ffn-stage').hidden = !length;

      $(this, '[data-ffn-empty]').hidden =
        Boolean(length);

      setText(
        $(this, '[data-ffn-counter]'),
        length
          ? `${this.index + 1} / ${length}`
          : ''
      );

      $$(
        this,
        '[data-ffn-prev],[data-ffn-next]'
      ).forEach(button => {
        button.disabled = length < 2;
      });

      if (!length) return;

      const picture = this.visible[this.index];
      const hero = $(this, '[data-ffn-hero]');

      if (hero.dataset.imageId !== picture.id) {
        hero.dataset.imageId = picture.id;

        if (
          !hero.src.includes(
            picture.src.split('?')[0]
          )
        ) {
          hero.srcset =
            `${picture.small} 640w, ` +
            `${picture.src} 1400w`;

          hero.sizes =
            '(min-width: 1024px) 52vw, 100vw';

          hero.src = picture.src;
        }

        hero.alt =
          picture.alt || 'Custom woven pet blanket';
      }

      $$(this, '.ffn-thumbs button').forEach(
        (button, i) => {
          button.setAttribute(
            'aria-current',
            String(i === this.index)
          );
        }
      );
    }
  }

  if (!customElements.get('ff-blanket-gallery')) {
    customElements.define(
      'ff-blanket-gallery',
      BlanketGallery
    );
  }

  if (!customElements.get('ff-blanket-native')) {
    customElements.define(
      'ff-blanket-native',
      BlanketNative
    );
  }
})();